import { useStep } from '@/hooks/useStep';
import { useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import { decodeUrl } from '@/utils/encodeUrl';

import {
	StepProfessionalList,
	StepScheduleUser,
	StepSuccess,
	TimelineLayout,
	TimelineStepProps,
} from '@/components';
import { PublicSessionAuth } from './PublicSessionAuth';
import { StepPayment } from '@/pages/user';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';

// Estendendo a interface para incluir a nova propriedade opcional
interface StepConfig extends TimelineStepProps {
	visible?: boolean;
}

const CreatePublicSession = () => {
	const { currentStep, setTotalStep, setCurrentStep, nextStep } = useStep();
	const [searchParams] = useSearchParams();
	const [isInitialized, setIsInitialized] = useState(false);

	// 1. Este é nosso array de configuração principal.
	// Adicionamos `visible: false` aos passos que queremos ocultar.
	const stepConfig: Array<StepConfig> = useMemo(
		() => [
			{
				title: 'Profissional',
				step: 1,
				current: currentStep === 1,
				children: <StepProfessionalList showBackButton={false} />,
				completed: currentStep > 1,
				visible: true,
			},
			{
				title: 'Data/Hora',
				step: 2,
				current: currentStep === 2,
				children: <StepScheduleUser isPublicView />,
				completed: currentStep > 2,
				visible: true,
			},
			{
				title: 'login',
				step: 3,
				current: currentStep === 3,
				children: <PublicSessionAuth />,
				completed: currentStep > 3,
				visible: false,
			},
			{
				title: 'Pagamento',
				step: 4,
				current: currentStep === 4,
				children: <StepPayment />,
				completed: currentStep > 4,
				visible: true,
			},
			{
				title: 'Sucesso',
				step: 5,
				current: currentStep === 5,
				children: <StepSuccess />,
				completed: currentStep === 5,
				isLast: true,
				visible: false,
			},
		],
		[currentStep],
	);

	const visualSteps = useMemo(() => {
		return stepConfig
			.filter((step) => step.visible)
			.map((step, index) => ({
				...step,
				step: index + 1,
				isLast:
					index === stepConfig.filter((s) => s.visible).length - 1,
			}));
	}, [stepConfig]);

	// 3. Encontramos o componente a ser renderizado a partir da lista completa.
	const currentStepComponent = useMemo(() => {
		return stepConfig.find((step) => step.current)?.children;
	}, [stepConfig]);

	useEffect(() => {
		const stepParam = searchParams.get('d');
		setTotalStep(stepConfig.length);
		const decodedParams = stepParam
			? decodeUrl<{ s: string }>(stepParam)
			: { s: '1' };
		const step = parseInt(decodedParams.s, 10);
		if (!Number.isNaN(step)) {
			setCurrentStep(step);
		}
		setIsInitialized(true);
	}, [searchParams, setCurrentStep, setTotalStep, stepConfig.length]);

	useEffect(() => {
		const sessionSuccess = searchParams.get('s_s') === 'true';
		if (sessionSuccess && currentStep === 3) {
			nextStep();
		}
	}, [searchParams, currentStep, nextStep]);

	if (!isInitialized) return null;

	/** Pós-Pagar: só login/cadastro, sem título "Agendar consulta" nem barra de passos. */
	if (currentStep === 3) {
		return (
			<section
				data-testid={E2E_SCHEDULING.WIZARD_ROOT}
				className="flex min-h-[calc(100vh-4rem)] w-full flex-1 items-center justify-center bg-zinc-50 px-4 py-8"
			>
				<PublicSessionAuth />
			</section>
		);
	}

	return (
		<section
			data-testid={E2E_SCHEDULING.WIZARD_ROOT}
			className="flex min-h-[calc(100vh-4rem)] px-2 pb-4 md:px-4"
		>
			<TimelineLayout stepList={visualSteps}>
				{currentStepComponent}
			</TimelineLayout>
		</section>
	);
};

export { CreatePublicSession };
